/**
 * Frontend timezone utilities for consistent time display across the application.
 *
 * This module provides timezone-aware formatting functions that work with
 * UTC ISO strings from the backend and display them in the user's local
 * timezone or a specific location's timezone.
 *
 * Uses date-fns-tz for timezone handling to match backend implementation.
 */

import { formatDistanceToNow, isToday, isTomorrow, parseISO } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';

/**
 * Time format options for display
 */
export type TimeFormat = '12h' | '24h';

/**
 * Get the user's detected timezone from the browser.
 * Falls back to UTC if detection fails.
 *
 * @returns IANA timezone identifier (e.g., "America/New_York")
 *
 * @example
 * const userTz = getUserTimezone();
 * // Returns: "America/Los_Angeles" or "UTC"
 */
export function getUserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.warn('Failed to detect user timezone, falling back to UTC:', error);
    return 'UTC';
  }
}

/**
 * Validate that a timezone string is a valid IANA timezone identifier.
 *
 * @param timezone - Timezone string to validate
 * @returns true if valid, false otherwise
 *
 * @example
 * isValidTimezone("America/New_York") // true
 * isValidTimezone("Invalid/Timezone") // false
 */
export function isValidTimezone(timezone: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Format a UTC ISO string to display time in a specific timezone.
 *
 * @param isoString - UTC ISO string (e.g., "2024-12-17T12:14:00.000Z")
 * @param timezone - IANA timezone identifier (e.g., "America/New_York")
 * @param timeFormat - 12h or 24h format (default: "12h")
 * @returns Formatted time string or "N/A" if invalid
 *
 * @example
 * formatTimeInTimezone("2024-12-17T12:14:00Z", "America/New_York", "12h")
 * // Returns: "7:14 AM"
 *
 * formatTimeInTimezone("2024-12-17T12:14:00Z", "America/New_York", "24h")
 * // Returns: "07:14"
 */
export function formatTimeInTimezone(
  isoString: string | null | undefined,
  timezone: string = getUserTimezone(),
  timeFormat: TimeFormat = '12h'
): string {
  if (!isoString) {
    return 'N/A';
  }

  try {
    // Validate timezone
    if (!isValidTimezone(timezone)) {
      console.warn(`Invalid timezone: ${timezone}, falling back to UTC`);
      timezone = 'UTC';
    }

    const formatString = timeFormat === '24h' ? 'HH:mm' : 'h:mm a';
    return formatInTimeZone(isoString, timezone, formatString);
  } catch (error) {
    console.error('Error formatting time:', error);
    return 'N/A';
  }
}

/**
 * Format a time range in a specific timezone.
 *
 * @param startIso - Start time UTC ISO string
 * @param endIso - End time UTC ISO string
 * @param timezone - IANA timezone identifier
 * @param timeFormat - 12h or 24h format (default: "12h")
 * @returns Formatted time range string or "N/A" if invalid
 *
 * @example
 * formatTimeRange("2024-12-17T11:44:00Z", "2024-12-17T12:14:00Z", "America/New_York")
 * // Returns: "6:44 AM - 7:14 AM"
 */
export function formatTimeRange(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  timezone: string = getUserTimezone(),
  timeFormat: TimeFormat = '12h'
): string {
  if (!startIso || !endIso) {
    return 'N/A';
  }

  try {
    const startTime = formatTimeInTimezone(startIso, timezone, timeFormat);
    const endTime = formatTimeInTimezone(endIso, timezone, timeFormat);

    if (startTime === 'N/A' || endTime === 'N/A') {
      return 'N/A';
    }

    return `${startTime} - ${endTime}`;
  } catch (error) {
    console.error('Error formatting time range:', error);
    return 'N/A';
  }
}

/**
 * Format a sun event time (sunrise/sunset) with timezone.
 * Shows date if not today, includes timezone abbreviation.
 *
 * @param isoString - UTC ISO string
 * @param timezone - IANA timezone identifier
 * @param timeFormat - 12h or 24h format (default: "12h")
 * @returns Formatted sun event time
 *
 * @example
 * formatSunEventTime("2024-12-17T12:14:00Z", "America/New_York")
 * // Returns: "7:14 AM EST" (if today)
 * // Returns: "Dec 17 at 7:14 AM EST" (if not today)
 */
export function formatSunEventTime(
  isoString: string | null | undefined,
  timezone: string = getUserTimezone(),
  timeFormat: TimeFormat = '12h'
): string {
  if (!isoString) {
    return 'N/A';
  }

  try {
    // Validate timezone
    if (!isValidTimezone(timezone)) {
      console.warn(`Invalid timezone: ${timezone}, falling back to UTC`);
      timezone = 'UTC';
    }

    const date = parseISO(isoString);
    const zonedDate = toZonedTime(date, timezone);

    const timeFormatString = timeFormat === '24h' ? 'HH:mm' : 'h:mm a';
    const tzAbbr = formatInTimeZone(isoString, timezone, 'zzz');

    // Check if the date is today in the target timezone
    if (isToday(zonedDate)) {
      const time = formatInTimeZone(isoString, timezone, timeFormatString);
      return `${time} ${tzAbbr}`;
    }

    // For future dates, include the date
    const dateStr = formatInTimeZone(isoString, timezone, 'MMM d');
    const time = formatInTimeZone(isoString, timezone, timeFormatString);
    return `${dateStr} at ${time} ${tzAbbr}`;
  } catch (error) {
    console.error('Error formatting sun event time:', error);
    return 'N/A';
  }
}

/**
 * Get a relative time description for a timestamp.
 * Shows "in X hours/minutes", "tomorrow at X", etc.
 *
 * @param isoString - UTC ISO string
 * @param timezone - IANA timezone identifier (for formatting specific times)
 * @param timeFormat - 12h or 24h format (default: "12h")
 * @returns Relative time description
 *
 * @example
 * getRelativeTimeDescription("2024-12-17T14:00:00Z", "America/New_York")
 * // Returns: "in 2 hours" (if current time is 12:00 PM)
 * // Returns: "tomorrow at 9:00 AM" (if tomorrow)
 * // Returns: "Friday at 9:00 AM" (if later this week)
 */
export function getRelativeTimeDescription(
  isoString: string | null | undefined,
  timezone: string = getUserTimezone(),
  timeFormat: TimeFormat = '12h'
): string {
  if (!isoString) {
    return 'N/A';
  }

  try {
    // Validate timezone
    if (!isValidTimezone(timezone)) {
      console.warn(`Invalid timezone: ${timezone}, falling back to UTC`);
      timezone = 'UTC';
    }

    const date = parseISO(isoString);
    const now = new Date();
    const zonedDate = toZonedTime(date, timezone);

    // If in the past, show "X ago"
    if (date < now) {
      return formatDistanceToNow(date, { addSuffix: true });
    }

    // If within 24 hours, show "in X hours/minutes"
    const hoursUntil = (date.getTime() - now.getTime()) / (1000 * 60 * 60);
    if (hoursUntil < 24) {
      return formatDistanceToNow(date, { addSuffix: true });
    }

    // If tomorrow, show "tomorrow at X"
    if (isTomorrow(zonedDate)) {
      const timeFormatString = timeFormat === '24h' ? 'HH:mm' : 'h:mm a';
      const time = formatInTimeZone(isoString, timezone, timeFormatString);
      return `tomorrow at ${time}`;
    }

    // If within 7 days, show "Monday at X"
    const daysUntil = hoursUntil / 24;
    if (daysUntil < 7) {
      const timeFormatString = timeFormat === '24h' ? 'HH:mm' : 'h:mm a';
      const dayAndTime = formatInTimeZone(isoString, timezone, `EEEE 'at' ${timeFormatString}`);
      return dayAndTime;
    }

    // For dates further out, show full date and time
    const timeFormatString = timeFormat === '24h' ? 'HH:mm' : 'h:mm a';
    const fullDateTime = formatInTimeZone(isoString, timezone, `MMM d 'at' ${timeFormatString}`);
    return fullDateTime;
  } catch (error) {
    console.error('Error getting relative time description:', error);
    return 'N/A';
  }
}

/**
 * Format a date string in a specific timezone.
 *
 * @param isoString - UTC ISO string
 * @param timezone - IANA timezone identifier
 * @param formatString - date-fns format string (default: "MMM d, yyyy")
 * @returns Formatted date string or "N/A" if invalid
 *
 * @example
 * formatDateInTimezone("2024-12-17T12:14:00Z", "America/New_York")
 * // Returns: "Dec 17, 2024"
 */
export function formatDateInTimezone(
  isoString: string | null | undefined,
  timezone: string = getUserTimezone(),
  formatString: string = 'MMM d, yyyy'
): string {
  if (!isoString) {
    return 'N/A';
  }

  try {
    // Validate timezone
    if (!isValidTimezone(timezone)) {
      console.warn(`Invalid timezone: ${timezone}, falling back to UTC`);
      timezone = 'UTC';
    }

    return formatInTimeZone(isoString, timezone, formatString);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'N/A';
  }
}

/**
 * Format a full date and time in a specific timezone.
 *
 * @param isoString - UTC ISO string
 * @param timezone - IANA timezone identifier
 * @param timeFormat - 12h or 24h format (default: "12h")
 * @returns Formatted date and time string or "N/A" if invalid
 *
 * @example
 * formatDateTimeInTimezone("2024-12-17T12:14:00Z", "America/New_York")
 * // Returns: "Dec 17, 2024 at 7:14 AM"
 */
export function formatDateTimeInTimezone(
  isoString: string | null | undefined,
  timezone: string = getUserTimezone(),
  timeFormat: TimeFormat = '12h'
): string {
  if (!isoString) {
    return 'N/A';
  }

  try {
    // Validate timezone
    if (!isValidTimezone(timezone)) {
      console.warn(`Invalid timezone: ${timezone}, falling back to UTC`);
      timezone = 'UTC';
    }

    const timeFormatString = timeFormat === '24h' ? 'HH:mm' : 'h:mm a';
    return formatInTimeZone(isoString, timezone, `MMM d, yyyy 'at' ${timeFormatString}`);
  } catch (error) {
    console.error('Error formatting date time:', error);
    return 'N/A';
  }
}

/**
 * Get the timezone offset string for display (e.g., "UTC-5", "UTC+8")
 *
 * @param timezone - IANA timezone identifier
 * @param date - Date to check offset for (defaults to now)
 * @returns Offset string like "UTC-5" or "UTC"
 *
 * @example
 * getTimezoneOffsetString("America/New_York")
 * // Returns: "UTC-5" (EST) or "UTC-4" (EDT)
 */
export function getTimezoneOffsetString(
  timezone: string = getUserTimezone(),
  date: Date = new Date()
): string {
  try {
    // Validate timezone
    if (!isValidTimezone(timezone)) {
      return 'UTC';
    }

    const offset = formatInTimeZone(date, timezone, 'XXX');

    // Convert offset format from "+05:00" to "UTC+5"
    if (offset === 'Z' || offset === '+00:00') {
      return 'UTC';
    }

    const hours = offset.substring(0, 3);
    const hoursNum = parseInt(hours, 10);

    if (hoursNum > 0) {
      return `UTC+${hoursNum}`;
    } else if (hoursNum < 0) {
      return `UTC${hoursNum}`;
    }

    return 'UTC';
  } catch (error) {
    console.error('Error getting timezone offset:', error);
    return 'UTC';
  }
}

/**
 * Parse a date string from various formats and return a proper Date object.
 * Handles edge cases and null/undefined values.
 *
 * @param dateString - Date string in various formats
 * @returns Date object or null if parsing fails
 *
 * @example
 * parseDateSafe("2024-12-17T12:14:00Z")
 * parseDateSafe(null) // returns null
 */
export function parseDateSafe(dateString: string | null | undefined): Date | null {
  if (!dateString) {
    return null;
  }

  try {
    const date = parseISO(dateString);
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return null;
    }
    return date;
  } catch (error) {
    console.error('Error parsing date:', error);
    return null;
  }
}

/**
 * Check if a time is in the past relative to now
 *
 * @param isoString - UTC ISO string
 * @returns true if the time is in the past, false otherwise
 */
export function isPast(isoString: string | null | undefined): boolean {
  if (!isoString) {
    return false;
  }

  try {
    const date = parseISO(isoString);
    return date < new Date();
  } catch (error) {
    console.error('Error checking if date is past:', error);
    return false;
  }
}

/**
 * Check if a time is currently happening (within a time window)
 *
 * @param startIso - Start time UTC ISO string
 * @param endIso - End time UTC ISO string
 * @returns true if current time is between start and end, false otherwise
 */
export function isHappeningNow(
  startIso: string | null | undefined,
  endIso: string | null | undefined
): boolean {
  if (!startIso || !endIso) {
    return false;
  }

  try {
    const now = new Date();
    const start = parseISO(startIso);
    const end = parseISO(endIso);
    return now >= start && now <= end;
  } catch (error) {
    console.error('Error checking if event is happening now:', error);
    return false;
  }
}

/**
 * Format a time with user's preferred time format (12h or 24h).
 * Uses the user's detected timezone.
 *
 * @param date - Date object to format
 * @param timeFormat - 12h or 24h format (default: "12h")
 * @returns Formatted time string
 *
 * @example
 * formatTimeWithPreference(new Date(), "12h")
 * // Returns: "7:14 AM"
 *
 * formatTimeWithPreference(new Date(), "24h")
 * // Returns: "07:14"
 */
export function formatTimeWithPreference(
  date: Date,
  timeFormat: TimeFormat = '12h'
): string {
  try {
    const timezone = getUserTimezone();
    const formatString = timeFormat === '24h' ? 'HH:mm' : 'h:mm a';
    return formatInTimeZone(date, timezone, formatString);
  } catch (error) {
    console.error('Error formatting time with preference:', error);
    return 'N/A';
  }
}
